import QorusClient from "@/api/clients/QorusClient";
import { Customer } from "@/models/customer";
import { i18n } from "vue-lang-router";
import { AxiosRequestConfig } from "axios";
import Locales from "@/utils/locales";
import store from "@/store";
import { CustomerMutations } from "@/store/customer/mutations";

export class AccountApi {
  static of(): AccountApi {
    return new AccountApi();
  }

  login(customer: Customer): Promise<Customer> {
    return QorusClient.client.post("customers/sign-in", customer).then(
      (data: Customer) => {
        const customer = Customer.of(data);

        if (!data.twoFactorAuthenticationEnabled) {
          store.dispatch('setCustomer', customer);
          if (data.customerGroup !== "General public") {
            store.dispatch('setCart', null);
          }
        }

        if (data.customerGroup !== "Empowerment") {
          sessionStorage.removeItem("empowerment");
          sessionStorage.removeItem("empowermentClient");
        }

        return customer;
      },
      data => {
        store.dispatch('setCustomer', null);
        throw data;
      }
    );
  }

  loginGpNet(customer: Customer): Promise<Customer> {
    return QorusClient.client.post("gpnet/sign-in", customer).then(
      (data: Customer) => {
        const customer = Customer.of(data);

        if (!data.twoFactorAuthenticationEnabled) {
          store.dispatch('setCustomer', customer);
        }

        if (data.customerGroup !== "General public") {
          store.dispatch('setCart', null);
        }

        if (data.customerGroup !== "Empowerment") {
          sessionStorage.removeItem("empowerment");
          sessionStorage.removeItem("empowermentClient");
        }

        return customer;
      },
      data => {
        store.dispatch('setCustomer', null);
        throw data;
      },
    );
  }

  loginHabile(authorizationCode): Promise<Customer> {
    const isEmpowerment = sessionStorage.getItem("empowerment");
    let habileType = "AIR_FRANCE_EMPLOYEE";
    if (isEmpowerment) {
      habileType = "EMPOWERMENT";
    }

    const axiosRequestConfig: AxiosRequestConfig = {
      params: {
        authorizationCode,
        habileType,
        subdomain: "NONE",
      },
    };
    return QorusClient.client.get("habile", axiosRequestConfig).then(
      (data: Customer) => {
        const customer = Customer.of(data);

        store.dispatch('setCustomer', customer);
        if (data.customerGroup !== "General public") {
          store.dispatch('setCart', null);
        }

        if (data.customerGroup !== "Empowerment") {
          sessionStorage.removeItem("empowerment");
          sessionStorage.removeItem("empowermentClient");
        } else {
          store.dispatch('setCart', null);
        }

        return customer;
      },
      data => {
        throw data;
      },
    );
  }

  private fullyURIDecode(data) {
    try {
      do {
        data = decodeURIComponent(data);
      } while (data !== decodeURIComponent(data))
    } catch (e) {
      console.error(`Could not decode this URI: ${data}\nReturning as is.`, e);
    }

    return data;
  }

  loginEmpowerment(params): Promise<unknown> {
    let dataEmpowerment;

    try {
      dataEmpowerment = {
        data: {
          context: JSON.parse(this.fullyURIDecode(atob(params.context))),
          client: JSON.parse(this.fullyURIDecode(atob(params.client))),
        },
      };

      return new Promise<string>((resolve) => {
        resolve(JSON.stringify(dataEmpowerment));
      }).then((data) => {

        sessionStorage.setItem("empowermentClient", data);
        sessionStorage.setItem("empowerment", "true");
        return data;
      })
        .catch((e) => {
          console.error(e);
          throw new Error("Something failed");
          // sessionStorage.removeItem("empowermentClient");
          //  sessionStorage.removeItem("empowerment");
        });
    } catch (e) {
      console.error(e);
      return new Promise<string>((resolve, reject) => {
        reject({ message: "JSON ERROR: " + e });
      });
    }
  }

  loginOneTimePassword(customer: Customer): Promise<Customer> {
    return QorusClient.client.post("customers/one-time-password",
      {
        email: customer.email,
        password: customer.password,
        oneTimePassword: customer.oneTimePassword,
      }
    ).then(
      (data: Customer) => {
        const customer = Customer.of(data);

        store.dispatch('setCustomer', customer);
        if (data.customerGroup !== "General public") {
          store.dispatch('setCart', null);
        }

        if (data.customerGroup !== "Empowerment") {
          sessionStorage.removeItem("empowerment");
          sessionStorage.removeItem("empowermentClient");
        }

        return customer;
      },
      data => {
        store.dispatch('setCustomer', null);
        throw data;
      }
    );
  }

  loginOneTimePasswordGpNet(customer: Customer): Promise<Customer> {
    return QorusClient.client.post("gpnet/one-time-password", {
      email: customer.email,
      password: customer.password,
      oneTimePassword: customer.oneTimePassword,
    }).then(
      (data: Customer) => {
        const customer = Customer.of(data);

        store.dispatch('setCustomer', customer);
        if (data.customerGroup !== "General public") {
          store.dispatch('setCart', null);
        }
        if (data.customerGroup !== "Empowerment") {
          sessionStorage.removeItem("empowerment");
          sessionStorage.removeItem("empowermentClient");
        }

        return customer;
      },
      data => {
        store.dispatch('setCustomer', null);
        throw data;
      },
    );
  }

  register(customer: Customer): Promise<Customer> {
    customer.locale = Locales.getLocaleCode(i18n.locale);

    return QorusClient.client.post("customers/register", customer).then(
      (data: Customer) => {
        const customer = Customer.of(data);

        store.dispatch('setCustomer', customer);
        if (data.customerGroup !== "General public") {
          store.dispatch('setCart', null);
        }

        if (data.customerGroup !== "Empowerment") {
          sessionStorage.removeItem("empowerment");
          sessionStorage.removeItem("empowermentClient");
        }

        return customer;
      },
      data => {
        store.dispatch('setCustomer', null);
        throw data;
      },
    );
  }

  registerGpNet(customer: Customer): Promise<Customer> {
    customer.locale = Locales.getLocaleCode(i18n.locale);

    return QorusClient.client.post("gpnet/register", customer).then(
      (data: Customer) => {
        const customer = Customer.of(data);

        store.dispatch('setCustomer', customer);
        if (data.customerGroup !== "General public") {
          store.dispatch('setCart', null);
        }

        if (data.customerGroup !== "Empowerment") {
          sessionStorage.removeItem("empowerment");
          sessionStorage.removeItem("empowermentClient");
        }

        return customer;
      },
      data => {
        store.dispatch('setCustomer', null);
        throw data;
      },
    );
  }

  resetPassword(customer: any): Promise<void> {
    const email = customer.email ? customer.email : "";
    const recaptchaData = customer.recaptchaData ? customer.recaptchaData : "";

    return QorusClient.client.post("customers/reset-password", { email, recaptchaData }).then(
      (data: void) => {
        return data;
      },
      data => {
        throw data;
      },
    );
  }

  resetPasswordGpNet(customer: any): Promise<void> {
    const email = customer.email ? customer.email : "";
    const recaptchaData = customer.recaptchaData ? customer.recaptchaData : "";

    return QorusClient.client.post("gpnet/reset-password", { email, recaptchaData }).then(
      (data: void) => {
        return data;
      },
      data => {
        throw data;
      },
    );
  }

  logout(): Promise<Customer> {
    sessionStorage.removeItem("empowerment");
    sessionStorage.removeItem("empowermentClient");
    sessionStorage.removeItem("configureEmpowerment");

    return QorusClient.client.post("customers/sign-out").then(
      (data) => {
        const customer = Customer.of(data);
        store.dispatch('setCustomer', customer);
        return customer;
      },
      data => {
        throw data;
      },
    );
  }

  getCustomer(): Promise<Customer> {
    return QorusClient.client.get("customers/me").then(
      (data) => {
        const customer = Customer.of(data);
        store.dispatch('setCustomer', customer);

        return customer;
      },
      data => {
        store.dispatch('setCustomer', null);
        throw data;
      },
    );
  }

  updateProfile(customer: Customer): Promise<any> {
    const params = {
      id: customer.id,
      firstName: customer.firstName,
      email: customer.email,
      lastName: customer.lastName,
      customerGroup: customer.customerGroup,
      birthDate: customer.birthDate,
      newsLetterSubscribed: customer.newsLetterSubscribed,
      locale: customer.locale,
      twoFactorAuthenticationEnabled: customer.twoFactorAuthenticationEnabled,
    };
    /* TODO send only form value not all customer information*/
    return QorusClient.client.patch("customers", params).then(
      () => {
        return;
      },
      err => {
        throw err;
      },
    );
  }

  updatePassword(passwords: { oldPassword: string, newPassword: string }): Promise<any> {
    return QorusClient.client.post("customers/change-password", passwords).then(
      (data) => {
        const customer = Customer.of(data);
        store.dispatch('setCustomer', customer);
      },
      err => {
        throw err;
      },
    );
  }

  addAddress(address): Promise<any> {
    const axiosRequestConfig: AxiosRequestConfig = {
      params: {
        defaultShipping: address.defaultShipping,
        defaultBilling: address.defaultBilling,
      },
    };
    return QorusClient.client.post("customers/address", address, axiosRequestConfig).then(
      (data) => {
        return data;
      },
      data => {
        throw data;
      },
    );
  }

  removeAddress(addressName: string): Promise<any> {
    return QorusClient.client.delete("customers/address/" + addressName).then(
      (data) => {
        return data;
      },
      data => {
        throw data;
      },
    );
  }

  changeResetPassword(params): Promise<any> {
    return QorusClient.client.post("customers/change-password/" + params.resetToken, {
      newPassword: params.newPassword,
      recaptchaData: params.recaptchaData,
    }).then(
      (data) => {
        return data;
      },
      data => {
        throw data;
      },
    );
  }

  subscribeNewsletter(customerEmail): Promise<any> {
    const axiosRequestConfig: AxiosRequestConfig = {
      params: {
        customerEmail,
      },
    };
    return QorusClient.client.put("customers/newsletter/subscribe", {}, axiosRequestConfig).then(
      (data) => {
        return data;
      },
      data => {
        throw data;
      },
    );
  }

}